import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatIDR } from "@/lib/format";
import { printSupplierDebtRecap } from "@/lib/invoice";
import { Printer, Table2, ArrowRightLeft } from "lucide-react";

const sum = (arr, f) => arr.reduce((s, x) => s + (f(x) || 0), 0);

export function buildDebtRows(bills, extraAdmins = [], partyKey = "supplier_name") {
  const map = {};
  extraAdmins.forEach((a) => { map[a] = []; });
  bills.forEach((b) => {
    const a = b.created_by_name || "Tanpa Admin";
    (map[a] = map[a] || []).push(b);
  });
  return Object.entries(map).map(([admin, bs]) => {
    const open = bs.filter((b) => b.payment_status !== "Lunas");
    return {
      admin,
      count: bs.length,
      suppliers: new Set(bs.map((b) => b[partyKey] || "-")).size,
      tagihan: sum(bs, (b) => b.amount),
      dibayar: sum(bs, (b) => b.paid),
      hutang: sum(open, (b) => b.remaining),
      overdue: sum(open.filter((b) => b.overdue), (b) => b.remaining),
    };
  }).sort((x, y) => y.hutang - x.hutang || x.admin.localeCompare(y.admin));
}

const COLS = (party, debt) => ["Admin", "Order", party, "Total Tagihan", "Dibayar", debt, "Jatuh Tempo"];

export const SupplierDebtRecap = ({ bills, extraAdmins, periodLabel, activeAdmin, onSelectAdmin, onReassign,
  title = "Rekap Hutang Supplier per Admin", partyLabel = "Supplier", debtLabel = "Sisa Hutang", partyKey = "supplier_name", testPrefix = "debt-recap" }) => {
  const rows = useMemo(() => buildDebtRows(bills, extraAdmins, partyKey), [bills, extraAdmins, partyKey]);
  const total = useMemo(() => ({
    count: sum(rows, (r) => r.count), tagihan: sum(rows, (r) => r.tagihan), dibayar: sum(rows, (r) => r.dibayar),
    hutang: sum(rows, (r) => r.hutang), overdue: sum(rows, (r) => r.overdue),
  }), [rows]);
  return (
    <Card className="overflow-hidden" data-testid={`${testPrefix}-card`}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b bg-blue-50/60 dark:bg-blue-950/30">
        <div className="flex items-center gap-2">
          <Table2 className="h-4 w-4 text-[#C9A227]" />
          <div>
            <p className="font-heading font-semibold text-sm">{title}</p>
            <p className="text-[11px] text-muted-foreground">{periodLabel} · {rows.length} admin · klik baris untuk melihat rinciannya</p>
          </div>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs" data-testid={`${testPrefix}-print-btn`}
          onClick={() => printSupplierDebtRecap({ periodLabel, rows, total, title, partyLabel, debtLabel })}>
          <Printer className="h-3.5 w-3.5" /> Cetak PDF
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm" data-testid={`${testPrefix}-table`}>
          <thead className="bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>{COLS(partyLabel, debtLabel).map((c, i) => <th key={c} className={`px-3 py-2 font-medium whitespace-nowrap ${i ? "text-right" : "text-left"}`}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.admin} onClick={() => onSelectAdmin(r.admin)} title="Lihat rincian tagihan admin ini"
                className={`border-t cursor-pointer transition-colors ${activeAdmin === r.admin ? "bg-amber-50 dark:bg-amber-950/30" : "hover:bg-muted/40"}`} data-testid={`${testPrefix}-row-${r.admin}`}>
                <td className="px-3 py-2 font-medium whitespace-nowrap">
                  <span className="underline decoration-dotted underline-offset-2">{r.admin}</span>
                  {r.admin === "Tanpa Admin" && onReassign && r.count > 0 && (
                    <Button size="sm" variant="outline" className="ml-2 h-6 px-2 text-[11px] gap-1" data-testid="debt-recap-reassign-btn"
                      onClick={(e) => { e.stopPropagation(); onReassign(); }}>
                      <ArrowRightLeft className="h-3 w-3" /> Pindahkan
                    </Button>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{r.count}</td>
                <td className="px-3 py-2 text-right">{r.suppliers}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{formatIDR(r.tagihan)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-emerald-600">{formatIDR(r.dibayar)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-rose-600" data-testid={`${testPrefix}-hutang-${r.admin}`}>{formatIDR(r.hutang)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap text-amber-600">{formatIDR(r.overdue)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 bg-[#0A1633] text-white font-semibold">
            <tr data-testid={`${testPrefix}-total`}>
              <td className="px-3 py-2">Total</td>
              <td className="px-3 py-2 text-right">{total.count}</td>
              <td className="px-3 py-2 text-right">-</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">{formatIDR(total.tagihan)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">{formatIDR(total.dibayar)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap text-[#C9A227]">{formatIDR(total.hutang)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">{formatIDR(total.overdue)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
};
